import {
  ChronologicalData,
  GroupedData,
} from '../utils/chronological-data.interface';
import { DateFormatter } from '../date-formatter.class';
import { GroupBy } from '../utils/group-by.enum';
import { GroupByStrategyBase } from './group-strategy-base.class';
import { GroupedChronologicalData } from '../utils/grouped-chronological-data.class';
import { DatesGroup } from '../utils/dates-group.interface';
import { getDataByDates } from '../utils/get-data-by-dates';
import { groupDatesByMonth } from '../utils/group-dates-by-month';

/* It takes an array of dates and groups them by month */
export class GroupByWeekStrategy<T> extends GroupByStrategyBase<T> {
  locale: string | undefined;

  /**
   * It takes an array of chronological data, a group by option, and an optional
   * locale, and returns a grouped chronological data by week
   * @param {ChronologicalData<T>[]} data - ChronologicalData<T>[]
   * @param {GroupBy} groupBy - GroupBy - this is the type of grouping you want to
   * do. It can be one of the following:
   * @param {string} [locale] - The locale to use for the date formatting.
   * @returns A GroupedChronologicalData<T> object.
   */
  group(
    data: ChronologicalData<T>[],
    groupBy: GroupBy,
    locale?: string
  ): GroupedChronologicalData<T> {
    this.locale = locale;
    const group = new GroupedChronologicalData<T>(groupBy);
    const dates = data.map((value) => value.date);

    const datesByWeek = this.groupDates(dates);
    const grupedData = datesByWeek.map((weekGroup) => {
      const label = weekGroup.label;
      const dates = weekGroup.dates;

      const dataGroup = getDataByDates(data, dates, this.locale);

      return {
        label,
        dates,
        data: dataGroup,
      } as GroupedData<T>;
    });
    group.data = grupedData;

    return group;
  }

  /**
   * It takes an array of dates, groups them by month, then groups each month's dates
   * by week, and finally flattens the array of arrays into a single array
   * @param {Date[]} dates - Date[] - an array of dates
   * @returns An array of arrays of dates grouped by week.
   */
  private groupDates(dates: Date[]) {
    const datesByMonth = groupDatesByMonth(dates, this.locale);
    const datesByWeek = datesByMonth.map((group) =>
      this.groupMonthDatesByWeek(group)
    );

    return datesByWeek.reduce((acc, current) => {
      return acc.concat(current);
    }, []);
  }

  /**
   * It takes a DatesGroup object and returns an array of DatesGroup objects, where
   * each DatesGroup object in the array has a label that includes the week number of
   * the month
   * @param {DatesGroup} datesGroup - DatesGroup
   * @returns An array of DatesGroup objects.
   */
  private groupMonthDatesByWeek(datesGroup: DatesGroup): DatesGroup[] {
    const groupsByWeek: DatesGroup[] = [];
    const weeks = Array.from(
      new Set(
        datesGroup.dates.map((date) =>
          new DateFormatter(this.locale).getWeekNumberOfMonth(date)
        )
      )
    );

    weeks.forEach((week) => {
      const group: DatesGroup = {
        label: `W${week}-` + datesGroup.label,
        dates: datesGroup.dates.filter((date) => {
          const dateWeek = new DateFormatter(this.locale).getWeekNumberOfMonth(
            date
          );
          return dateWeek === week;
        }),
      };
      groupsByWeek.push(group);
    });

    return groupsByWeek;
  }
}
